import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, BellRing, Check, Gift, LayoutDashboard, Mail, PenLine, QrCode, Reply, Send, X } from 'lucide-react';
import { useMarkBioSharedMutation, useMeQuery } from '../profiles/api';
import { Avatar } from '../profiles/avatar';
import { useMyStampsQuery, useStampBookQuery } from '../letters/api';
import { arrivesPhrase } from '../letters/eta';
import { routeToHash } from '../letters/route';
import { useLastingLinkQuery } from '../inviteLinks/api';
import { usePushNotifications } from '../notifications/usePushNotifications';
import { ITEMS, type Item } from '../onboarding/GettingStarted';
import { InviteChoices } from '../onboarding/InviteChoices';
import { copy, CopyButton, ShareKitSheet, ShowMyToiyp } from '../onboarding/ShareKit';
import { DeliveredPromptCard, useDeliveredPrompt } from './DeliveredPrompt';
import { track } from '../onboarding/track';
import { qrMatrix, qrPath } from '../onboarding/qr';
import { BIO_LINES, toiypAddress, withVia } from '../onboarding/share';
import { useResendVerification } from '../onboarding/verify';
import { PostageStamp } from '../postage/Stamp';
import { openStampBook } from '../postage/StampBook';
import { GiftStampBook } from '../postage/GiftStampBook';
import { readChosen } from '../postage/designs';
import {
  appDay,
  dayPart,
  daysAgo,
  greetingName,
  inThePost,
  landedPhrase,
  nudgeCandidates,
  nudgeContext,
  pickNudge,
  profileProgress,
  stampSummary,
  todayTasks,
  whatsNew,
  type FeatureCard,
  type NudgeDismissal,
  type PanelConversation,
  type PostItem,
  type ProfileProgress,
  type StampSummary,
  type TodayTask,
} from './homeData';
import {
  trackHomePanelAction,
  trackHomePanelViewed,
  useHomePanelVariant,
  type HomePanelAction,
  type HomePanelSurface,
  type HomePanelVariant,
} from './variant';

/**
 * The wide side panel with nothing open (desktop only, ≥1024px). Two designs
 * under test (`home-panel-variant`):
 *
 *  - desk — a greeting, your address card, and a grid of tiles: finish your
 *    profile, your stamp book, new in toiyp, in the post.
 *  - noticeboard — what's new and a short list of little things to do on the
 *    left; your QR, stamp book and what's in the post on the right.
 *
 * Both are built only from what the app already knows; a tile with nothing
 * real to say is left out.
 */

const POST_OFFICE_HANDLE = 'postmaster';

/** Short names for the progress chips and the CTA for each next step. */
const STEP: Record<Item, { chip: string; cta: string }> = {
  photo: { chip: 'Photo', cta: 'Add a photo' },
  bio: { chip: 'A line about you', cta: 'Write a line' },
  invited: { chip: 'Invite someone', cta: 'Invite someone' },
  bioShared: { chip: 'In your bio', cta: 'Copy a bio line' },
  emailConfirmed: { chip: 'Email', cta: 'Resend the email' },
  firstLetter: { chip: 'First letter', cta: 'Write a letter' },
};

function itemOf(key: Item) {
  return ITEMS.find((i) => i.key === key)!;
}

function handleName(peer: { handle: string | null; displayName: string | null }): string {
  return peer.handle ? `@${peer.handle}` : (peer.displayName ?? 'someone');
}

/** What a task row says. Slow-mail tone: no hurry, and never a read tick. */
function taskCopy(task: TodayTask): { Icon: typeof Mail; title: string; note: string; cta: string } {
  switch (task.kind) {
    case 'open':
      return {
        Icon: Mail,
        title: `A letter from ${task.peerName} has arrived`,
        note: task.count > 1 ? `${task.count} letters are waiting to be opened.` : 'It’s waiting to be opened.',
        cta: 'Open',
      };
    case 'reply':
      return {
        Icon: Reply,
        title: `Reply to ${task.peerName}’s letter`,
        note: task.deliveredAt ? `It landed ${daysAgo(task.deliveredAt)}. Take your time.` : 'Take your time.',
        cta: 'Reply',
      };
    case 'finish-reply':
      return {
        Icon: PenLine,
        title: `Finish your reply to ${task.peerName}`,
        note: 'Your draft is kept on this device.',
        cta: 'Continue',
      };
    case 'finish-letter':
      return {
        Icon: PenLine,
        title: `Finish your letter to ${task.peerName}`,
        note: 'Your draft is kept on this device.',
        cta: 'Continue',
      };
    case 'push':
      return {
        Icon: BellRing,
        title: 'Hear when a letter arrives',
        note: 'One quiet note on this device when something lands. Never the letter itself.',
        cta: 'Turn on',
      };
  }
}

/** How many tasks a list shows before it stops. */
const MAX_TASKS = 4;

interface DeskProps {
  conversations: PanelConversation[];
  onOpenProfile: () => void;
  /** Opens "Write to someone new". */
  onWrite: () => void;
  onOpenConversation: (relationshipId: string) => void;
}

/**
 * Everything the desk surfaces share — data, actions, tracking, and the sheets
 * they open — so the wide panel and the phone versions say the same things.
 * `surface` tells the view event where it was seen.
 */
function useDesk(
  { conversations, onOpenProfile, onWrite, onOpenConversation }: DeskProps,
  surface: HomePanelSurface,
) {
  const { variant, settled } = useHomePanelVariant();
  const viewed = useRef(false);
  useEffect(() => {
    if (!settled || viewed.current) return;
    viewed.current = true;
    trackHomePanelViewed(variant, surface);
  }, [settled, variant, surface]);

  const { data: me } = useMeQuery();
  const { data: book } = useStampBookQuery();
  const { data: stamps } = useMyStampsQuery();
  const resend = useResendVerification();
  const push = usePushNotifications();
  const [pushMessage, setPushMessage] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [giftTo, setGiftTo] = useState<string | null>(null);

  const act = (action: HomePanelAction, extra?: Record<string, string>) =>
    trackHomePanelAction(variant, action, { surface, ...extra });

  const progress = profileProgress(me?.gettingStarted);
  const post = useMemo(() => inThePost(conversations), [conversations]);
  const stampsAtAGlance = stampSummary(book, stamps, readChosen());
  const features = whatsNew(stamps);
  // Only while this device could say yes: supported, set up on the server, not
  // yet asked or granted (granted heals itself quietly in the hook).
  const offerPush = !push.configLoading && push.status === 'default' && push.permission !== 'granted';
  const tasks = useMemo(() => todayTasks(conversations, { offerPush }), [conversations, offerPush]);

  const doTask = (task: TodayTask) => {
    if (task.kind === 'push') {
      act('enable_push');
      setPushMessage(null);
      void push.enable().then((result) => {
        if (!result) return;
        track({ event: 'push_permission_result', result, reason: 'home' });
        setPushMessage(
          result === 'granted'
            ? 'Done. This device will hear when a letter arrives.'
            : 'Your browser said no. You can change that in its site settings.',
        );
      });
      return;
    }
    act(task.kind === 'open' ? 'open_unread' : 'today_task', { task: task.kind });
    if (task.kind === 'reply' || task.kind === 'finish-reply') {
      // Replies are written from their letter, so that's where it opens.
      window.location.hash = routeToHash({ name: 'letter', letterId: task.letterId, relationshipId: task.relationshipId });
    } else {
      onOpenConversation(task.relationshipId);
    }
  };

  const doStep = (key: Item, action: HomePanelAction) => {
    act(action, { step: key });
    if (key === 'photo' || key === 'bio') onOpenProfile();
    else if (key === 'invited') setInviting((v) => !v);
    else if (key === 'bioShared') setSharing(true);
    else if (key === 'emailConfirmed') void resend.send();
    else onWrite();
  };

  const shared: Shared = {
    variant,
    act,
    handle: me?.handle ?? null,
    progress,
    post,
    stamps: stampsAtAGlance,
    features,
    inviting,
    resendMessage: resend.message ?? pushMessage,
    doStep,
    tasks,
    doTask,
    pushBusy: push.busy,
    onShare: () => {
      act('share_toiyp');
      setSharing(true);
    },
    onShowQr: () => {
      act('show_qr');
      setShowQr(true);
    },
    onOpenStampBook: (action = 'open_stamp_book', extra) => {
      act(action, extra);
      openStampBook();
    },
    onOpenConversation: (id, action) => {
      act(action);
      onOpenConversation(id);
    },
  };

  const overlays = (
    <>
      {sharing && <ShareKitSheet from="home_panel" onClose={() => setSharing(false)} />}
      {showQr && <ShowMyToiyp onClose={() => setShowQr(false)} />}
      {giftTo && <GiftStampBook relationshipId={giftTo} onClose={() => setGiftTo(null)} />}
    </>
  );

  return {
    me,
    shared,
    overlays,
    giftable: Boolean(stamps?.plus && stamps.collections.some((c) => c.plusOnly)),
    onGift: (id: string) => {
      act('gift_stamp_book');
      setGiftTo(id);
    },
  };
}

/** The wide side panel with nothing open (≥1024px). */
export function HomePanel(props: DeskProps) {
  const { me, shared, overlays, giftable, onGift } = useDesk(props, 'panel');
  if (!me?.handle) return null;
  return (
    <section className={`screen home-panel home-panel--${shared.variant}`} aria-label="Nothing open">
      {shared.variant === 'noticeboard' ? (
        <Noticeboard {...shared} conversations={props.conversations} giftable={giftable} onGift={onGift} />
      ) : (
        <Desk {...shared} name={greetingName(me.displayName, me.handle)} />
      )}
      {overlays}
    </section>
  );
}

/* ── Phone, variant desk: a strip of cards above the letters list ────────── */

export function MobileDeskStrip(props: DeskProps) {
  const { me, shared: s, overlays } = useDesk(props, 'mobile_strip');
  if (!me?.handle) return null;
  const feature = s.features[0] ?? null;
  const next = s.progress?.next ?? null;
  return (
    <section className="desk-strip" aria-label="Your desk">
      <h1 className="desk-strip__title">Who’s your toiyp today?</h1>
      <div className="desk-strip__row">
        <div className="desk-strip__card desk-strip__card--toiyp">
          <h2 className="desk-strip__label">Your toiyp</h2>
          <p className="desk-strip__address">{toiypAddress(me.handle)}</p>
          <div className="desk-strip__actions">
            <button type="button" className="home-btn home-btn--ink desk-strip__share" onClick={s.onShare}>
              Share
            </button>
            <button type="button" className="desk-strip__qr" onClick={s.onShowQr} aria-label="Show my toiyp code">
              <QrCode size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
        {s.tasks[0] && <StripTaskCard task={s.tasks[0]} more={s.tasks.length - 1} busy={s.pushBusy} onDo={s.doTask} />}
        {s.progress && next && (
          <div className="desk-strip__card">
            <h2 className="desk-strip__name">Finish your profile</h2>
            <div className="home-progress home-progress--thin" aria-hidden="true">
              <span style={{ width: `${Math.round((s.progress.done.length / s.progress.total) * 100)}%` }} />
            </div>
            <p className="desk-strip__note">
              {s.progress.done.length} of {s.progress.total} · next, {STEP[next].chip.toLowerCase()}
            </p>
            <button type="button" className="home-link desk-strip__foot" onClick={() => s.doStep(next, 'profile_step')}>
              {STEP[next].cta} <ArrowRight size={14} aria-hidden="true" />
            </button>
          </div>
        )}
        {feature && (
          <button
            type="button"
            className="desk-strip__card desk-strip__card--ink"
            onClick={() => s.onOpenStampBook('whats_new', { feature: feature.id })}
          >
            <span className="home-chip home-chip--new">New</span>
            <span className="desk-strip__name">{feature.title}</span>
            {feature.plus && <span className="home-tile__plus">toiyp Plus</span>}
          </button>
        )}
      </div>
      {s.inviting && s.handle && (
        <div className="desk-strip__invite">
          <InviteChoices
            handle={s.handle}
            onStarted={(id) => {
              window.location.hash = `#/c/${id}`;
            }}
          />
        </div>
      )}
      {s.resendMessage && (
        <p className="home-panel__status desk-strip__status" role="status">
          {s.resendMessage}
        </p>
      )}
      {overlays}
    </section>
  );
}

function StripTaskCard({
  task,
  more,
  busy,
  onDo,
}: {
  task: TodayTask;
  more: number;
  busy: boolean;
  onDo: (task: TodayTask) => void;
}) {
  const c = taskCopy(task);
  return (
    <div className="desk-strip__card desk-strip__card--task">
      <h2 className="desk-strip__label">{more > 0 ? `To do · ${more + 1}` : 'To do'}</h2>
      <p className="desk-strip__name">{c.title}</p>
      <button
        type="button"
        className="home-link desk-strip__foot"
        disabled={task.kind === 'push' && busy}
        onClick={() => onDo(task)}
      >
        {c.cta} <ArrowRight size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

/* ── Short phones, both variants: one nudge in the letters list ────────── */

const NUDGE_KEY = 'toiyp:nudge-dismissed';

function readDismissal(): NudgeDismissal | null {
  try {
    const v = localStorage.getItem(NUDGE_KEY);
    return v ? (JSON.parse(v) as NudgeDismissal) : null;
  } catch {
    return null;
  }
}

function writeDismissal(d: NudgeDismissal): void {
  try {
    localStorage.setItem(NUDGE_KEY, JSON.stringify(d));
  } catch {
    /* private mode: it comes back on the next visit, which is fine */
  }
}

/**
 * On a short phone the strip and the Desk bar would squeeze the list, so
 * instead one card sits after the first letter: the single most useful thing
 * to do, "See all" for the rest, and "Not now" until tomorrow.
 */
export function MobileNudge(props: DeskProps & { onSeeAll: () => void }) {
  const { me, shared: s, overlays } = useDesk(props, 'mobile_nudge');
  const [dismissed, setDismissed] = useState(readDismissal);
  const [markBioShared] = useMarkBioSharedMutation();
  const [copied, setCopied] = useState(false);
  const delivered = useDeliveredPrompt(props.conversations);
  const candidates = nudgeCandidates(
    props.conversations,
    s.progress,
    s.features,
    delivered.round,
    s.tasks.some((t) => t.kind === 'push'),
  );
  const item = pickNudge(candidates, dismissed, new Date());
  if (!me?.handle || !item) return null;
  const handle = me.handle;

  if (item.kind === 'delivered') {
    return (
      <DeliveredPromptCard
        round={item.round}
        surface="mobile_nudge"
        onDismiss={() => {
          // A week for this prompt, and the rest of today for the nudge, so
          // putting it away doesn't just bring the next card up.
          delivered.dismiss();
          const d = { day: appDay(new Date()), id: item.id };
          writeDismissal(d);
          setDismissed(d);
        }}
      />
    );
  }

  const bioLine = BIO_LINES[0]!.line(handle);
  let context = nudgeContext(s.post);
  let title: string;
  let detail: ReactNode = null;
  let cta: string;
  let onCta: () => void;
  if (item.kind === 'task') {
    const c = taskCopy(item.task);
    const task = item.task;
    if (task.kind === 'open') context = 'In your letterbox';
    else if (task.kind !== 'push') context = 'Your turn';
    title = c.title;
    detail = task.kind === 'open' ? null : <p className="nudge__note">{c.note}</p>;
    cta = task.kind === 'open' ? 'Open it' : c.cta;
    onCta = () => s.doTask(task);
  } else if (item.kind === 'step' && item.step === 'bioShared') {
    title = itemOf(item.step).title;
    detail = <p className="nudge__line">{bioLine}</p>;
    cta = copied ? 'Copied' : 'Copy bio line';
    onCta = () =>
      void copy(bioLine).then((ok) => {
        if (!ok) return;
        s.act('copy_bio_line');
        track({ event: 'bio_line_copied', platform: BIO_LINES[0]!.platform });
        void markBioShared();
        setCopied(true);
      });
  } else if (item.kind === 'step') {
    title = itemOf(item.step).title;
    detail = <p className="nudge__note">{itemOf(item.step).note}</p>;
    cta = STEP[item.step].cta;
    onCta = () => s.doStep(item.step, 'todo_item');
  } else if (item.kind === 'share') {
    title = 'Hand out your toiyp';
    detail = <p className="nudge__line">Don’t text me, toiyp me → {toiypAddress(handle)}</p>;
    cta = 'Share my toiyp';
    onCta = s.onShare;
  } else {
    title = item.feature.title;
    detail = <p className="nudge__note">{item.feature.body}</p>;
    cta = item.feature.cta;
    onCta = () => s.onOpenStampBook('whats_new', { feature: item.feature.id });
  }

  return (
    <section className="nudge" aria-labelledby="nudge-title">
      <button
        type="button"
        className="nudge__close"
        aria-label="Not now"
        onClick={() => {
          s.act('nudge_dismiss', { item: item.id });
          const d = { day: appDay(new Date()), id: item.id };
          writeDismissal(d);
          setDismissed(d);
        }}
      >
        <X size={16} aria-hidden="true" />
      </button>
      <p className="nudge__context">{context}</p>
      <h2 className="nudge__title" id="nudge-title">
        {title}
      </h2>
      {detail}
      <div className="nudge__actions">
        <button
          type="button"
          className="home-btn home-btn--ink nudge__cta"
          aria-expanded={item.kind === 'step' && item.step === 'invited' ? s.inviting : undefined}
          onClick={onCta}
        >
          {cta}
        </button>
        {candidates.length > 1 && (
          <button
            type="button"
            className="home-link nudge__all"
            onClick={() => {
              s.act('nudge_see_all');
              props.onSeeAll();
            }}
          >
            See all ({candidates.length})
          </button>
        )}
      </div>
      {item.kind === 'step' && item.step === 'invited' && s.inviting && (
        <div className="nudge__invite">
          <InviteChoices
            handle={handle}
            onStarted={(id) => {
              window.location.hash = `#/c/${id}`;
            }}
          />
        </div>
      )}
      {s.resendMessage && (
        <p className="nudge__note" role="status">
          {s.resendMessage}
        </p>
      )}
      {overlays}
    </section>
  );
}

/* ── Phone, variant noticeboard: the Desk tab ───────────────────────────── */

const DESK_SEEN_KEY = 'toiyp:desk-seen';

function readDeskSeen(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DESK_SEEN_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}

/** The Desk tab's dot: a feature card this person hasn't seen on the Desk yet. */
export function useDeskHasNew(): boolean {
  const { data: stamps } = useMyStampsQuery();
  const ids = whatsNew(stamps).map((f) => f.id);
  const seen = readDeskSeen();
  return ids.some((id) => !seen.includes(id));
}

export function MobileDeskScreen(props: DeskProps & { onLetters: () => void; short?: boolean }) {
  const { me, shared: s, overlays, giftable, onGift } = useDesk(props, 'mobile_desk');
  useEffect(() => {
    if (s.features.length === 0) return;
    try {
      localStorage.setItem(DESK_SEEN_KEY, JSON.stringify(s.features.map((f) => f.id)));
    } catch {
      /* private mode: the dot just stays */
    }
  }, [s.features]);
  if (!me?.handle) return null;
  return (
    <section className="screen mobile-desk" aria-label="Your desk">
      <header className="mobile-desk__head">
        {props.short && (
          <button type="button" className="screen__back" onClick={props.onLetters} aria-label="Back to your letters">
            <ArrowLeft size={18} />
          </button>
        )}
        <h1 className="mobile-desk__title">Your desk</h1>
      </header>
      <div className="mobile-desk__body">
        {s.features.length > 0 && <WhatsNew {...s} />}
        <MobileQrCard handle={me.handle} onShare={s.onShare} onShowQr={s.onShowQr} />
        <ThingsToDo {...s} conversations={props.conversations} giftable={giftable} onGift={onGift} />
        {s.stamps && (
          <section className="home-tile mobile-desk__stamps" aria-label="Stamp book">
            <MiniStamps stamps={s.stamps} size="small" />
            <span className="mobile-desk__stamps-text">
              <span className="home-tile__title">Stamp book</span>
              <span className="home-tile__note">
                {s.stamps.nextAllowanceOn
                  ? `${s.stamps.express} Express left · more on ${dayMonth(s.stamps.nextAllowanceOn, 'short')}`
                  : 'Your stamp designs'}
              </span>
            </span>
            <button type="button" className="home-link" onClick={() => s.onOpenStampBook()}>
              Open
            </button>
          </section>
        )}
        {s.resendMessage && (
          <p className="home-panel__status" role="status">
            {s.resendMessage}
          </p>
        )}
      </div>
      {!props.short && (
        <DeskTabBar active="desk" onLetters={props.onLetters} onWrite={props.onWrite} onDesk={() => {}} />
      )}
      {overlays}
    </section>
  );
}

function MobileQrCard({ handle, onShare, onShowQr }: { handle: string; onShare: () => void; onShowQr: () => void }) {
  const { data: link } = useLastingLinkQuery();
  const matrix = useMemo(() => (link ? qrMatrix(withVia(link.url, 'qr')) : null), [link]);
  return (
    <section className="mobile-qr" aria-labelledby="mobile-qr-title">
      {matrix && (
        <button type="button" className="mobile-qr__code" onClick={onShowQr} aria-label="Show my toiyp code full screen">
          <svg viewBox={`0 0 ${matrix.length} ${matrix.length}`} aria-hidden="true" shapeRendering="crispEdges">
            <path d={qrPath(matrix)} fill="currentColor" />
          </svg>
        </button>
      )}
      <span className="mobile-qr__text">
        <span className="board-qr__label" id="mobile-qr-title">
          What’s your toiyp?
        </span>
        <span className="mobile-qr__address">{toiypAddress(handle)}</span>
      </span>
      <button type="button" className="home-btn home-btn--ink" onClick={onShare}>
        Share
      </button>
    </section>
  );
}

/**
 * The noticeboard variant's bottom bar on a phone: Letters, an ink Write
 * button in the middle, and the Desk. Replaces the floating pill tab bar;
 * your profile stays one tap away from the header.
 */
export function DeskTabBar({
  active,
  onLetters,
  onWrite,
  onDesk,
  hasNew = false,
}: {
  active: 'letters' | 'desk';
  onLetters: () => void;
  onWrite: () => void;
  onDesk: () => void;
  hasNew?: boolean;
}) {
  return (
    <nav className="desk-tabs" aria-label="Main">
      <button
        type="button"
        className={`desk-tabs__tab${active === 'letters' ? ' is-on' : ''}`}
        aria-current={active === 'letters' ? 'page' : undefined}
        onClick={onLetters}
      >
        <Mail size={22} aria-hidden="true" />
        Letters
      </button>
      <button type="button" className="desk-tabs__write" onClick={onWrite} aria-label="Write a letter">
        <PenLine size={22} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`desk-tabs__tab${active === 'desk' ? ' is-on' : ''}`}
        aria-current={active === 'desk' ? 'page' : undefined}
        onClick={onDesk}
      >
        <LayoutDashboard size={22} aria-hidden="true" />
        Desk
        {hasNew && active !== 'desk' && <span className="desk-tabs__dot" aria-label="Something new" />}
      </button>
    </nav>
  );
}

interface Shared {
  variant: HomePanelVariant;
  act: (action: HomePanelAction, extra?: Record<string, string>) => void;
  handle: string | null;
  progress: ProfileProgress | null;
  post: PostItem[];
  stamps: StampSummary | null;
  features: FeatureCard[];
  inviting: boolean;
  resendMessage: string | null | undefined;
  doStep: (key: Item, action: HomePanelAction) => void;
  tasks: TodayTask[];
  doTask: (task: TodayTask) => void;
  pushBusy: boolean;
  onShare: () => void;
  onShowQr: () => void;
  onOpenStampBook: (action?: HomePanelAction, extra?: Record<string, string>) => void;
  onOpenConversation: (relationshipId: string, action: HomePanelAction) => void;
}

/* ── A · Your desk ───────────────────────────────────────────────────────── */

function Desk({ name, ...s }: Shared & { name: string | null }) {
  const now = new Date();
  const feature = s.features[0] ?? null;
  return (
    <div className="desk">
      <header className="desk__hello">
        <p className="home-panel__eyebrow">{dayPart(now)}</p>
        <h1 className="desk__title">Who’s your toiyp today{name ? `, ${name}` : ''}?</h1>
        <p className="desk__lead">No letter open. Here’s what’s on your desk.</p>
      </header>

      {s.handle && (
        <section className="desk-address" aria-labelledby="desk-address-title">
          <div className="desk-address__text">
            <h2 className="desk-address__label" id="desk-address-title">
              Your toiyp
            </h2>
            <p className="desk-address__address">
              {s.handle}
              <span>@toiyp.me</span>
            </p>
            <p className="desk-address__line">Don’t text me, toiyp me. Hand it out instead of your number.</p>
            <div className="desk-address__actions">
              <button type="button" className="home-btn home-btn--ink" onClick={s.onShare}>
                <Send size={16} aria-hidden="true" />
                Share my toiyp
              </button>
              <CopyButton
                className="home-btn home-btn--soft"
                text={toiypAddress(s.handle)}
                label="Copy your toiyp address"
                onCopied={() => s.act('copy_address')}
              />
              <button type="button" className="home-btn home-btn--soft" onClick={s.onShowQr}>
                <QrCode size={16} aria-hidden="true" />
                Show QR
              </button>
            </div>
          </div>
          <Postmark date={now} />
        </section>
      )}

      <div className="desk__tiles">
        {s.tasks.length > 0 && (
          <section className="home-tile board-todo desk-today" aria-labelledby="desk-today-title">
            <div className="home-tile__head">
              <h2 className="home-tile__title" id="desk-today-title">
                To do today
              </h2>
              {s.tasks.length > MAX_TASKS && <span className="home-tile__count">{s.tasks.length} things</span>}
            </div>
            <ul className="board-todo__list">
              {s.tasks.slice(0, MAX_TASKS).map((t) => (
                <TaskRow key={t.id} task={t} busy={s.pushBusy} onDo={s.doTask} />
              ))}
            </ul>
          </section>
        )}
        {s.progress && <ProfileTile {...s} progress={s.progress} />}
        {s.stamps && (
          <section className="home-tile" aria-labelledby="desk-stamps-title">
            <div className="home-tile__head">
              <h2 className="home-tile__title" id="desk-stamps-title">
                Your stamp book
              </h2>
              {s.stamps.express > 0 && (
                <span className="home-chip home-chip--blush">{s.stamps.express} Express left</span>
              )}
            </div>
            <MiniStamps stamps={s.stamps} size="sheet" />
            <p className="home-tile__note">{stampsNote(s.stamps, 'Next stamps arrive on')}</p>
            <button type="button" className="home-link home-tile__foot" onClick={() => s.onOpenStampBook()}>
              Open stamp book <ArrowRight size={14} aria-hidden="true" />
            </button>
          </section>
        )}
        {feature && (
          <section className="home-tile home-tile--ink" aria-labelledby="desk-new-title">
            <div className="home-tile__tags">
              <span className="home-chip home-chip--new">New</span>
              {feature.plus && <span className="home-tile__plus">toiyp Plus</span>}
            </div>
            <h2 className="home-tile__title" id="desk-new-title">
              {feature.title}
            </h2>
            <p className="home-tile__note">{feature.body}</p>
            <button
              type="button"
              className="home-link home-link--light home-tile__foot"
              onClick={() => s.onOpenStampBook('whats_new', { feature: feature.id })}
            >
              Take a look <ArrowRight size={14} aria-hidden="true" />
            </button>
          </section>
        )}
        {s.post.length > 0 && (
          <section className="home-tile" aria-labelledby="desk-post-title">
            <h2 className="home-tile__title" id="desk-post-title">
              In the post
            </h2>
            <PostList post={s.post} onOpen={(id) => s.onOpenConversation(id, 'in_the_post_open')} />
          </section>
        )}
      </div>
      {s.resendMessage && (
        <p className="home-panel__status" role="status">
          {s.resendMessage}
        </p>
      )}
    </div>
  );
}

function ProfileTile({ progress, inviting, handle, doStep }: Shared & { progress: ProfileProgress }) {
  const next = progress.next!;
  const pct = Math.round((progress.done.length / progress.total) * 100);
  return (
    <section className="home-tile" aria-labelledby="desk-profile-title">
      <div className="home-tile__head">
        <h2 className="home-tile__title" id="desk-profile-title">
          Finish your profile
        </h2>
        <span className="home-tile__count">
          {progress.done.length} of {progress.total}
        </span>
      </div>
      <div
        className="home-progress"
        role="progressbar"
        aria-label="Getting started"
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.done.length}
      >
        <span style={{ width: `${pct}%` }} />
      </div>
      <p className="home-tile__note">
        Next up: {itemOf(next).title.toLowerCase()}. {itemOf(next).note}
      </p>
      <ul className="home-steps" aria-label="Steps">
        {ITEMS.map(({ key }) => {
          const done = progress.done.includes(key);
          return (
            <li key={key} className={`home-step${done ? ' home-step--done' : ''}`}>
              {done && <Check size={12} aria-hidden="true" />}
              {STEP[key].chip}
              <span className="visually-hidden">{done ? ', done' : ', to do'}</span>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="home-btn home-btn--ink home-tile__foot"
        aria-expanded={next === 'invited' ? inviting : undefined}
        onClick={() => doStep(next, 'profile_step')}
      >
        {STEP[next].cta}
      </button>
      {next === 'invited' && inviting && handle && (
        <div className="home-tile__invite">
          <InviteChoices
            handle={handle}
            onStarted={(id) => {
              window.location.hash = `#/c/${id}`;
            }}
          />
        </div>
      )}
    </section>
  );
}

/* ── C · Noticeboard ─────────────────────────────────────────────────────── */

function Noticeboard({
  conversations,
  giftable,
  onGift,
  ...s
}: Shared & { conversations: PanelConversation[]; giftable: boolean; onGift: (relationshipId: string) => void }) {
  return (
    <div className="board">
      <div className="board__main">
        {s.features.length > 0 && <WhatsNew {...s} />}
        <ThingsToDo {...s} conversations={conversations} giftable={giftable} onGift={onGift} />
      </div>
      <div className="board__side">
        {s.handle && <QrCard handle={s.handle} onShare={s.onShare} onShowQr={s.onShowQr} />}
        {s.stamps && (
          <section className="home-tile home-tile--compact" aria-labelledby="board-stamps-title">
            <div className="home-tile__head">
              <h2 className="home-tile__title" id="board-stamps-title">
                Stamp book
              </h2>
              <button type="button" className="home-link" onClick={() => s.onOpenStampBook()}>
                Open
              </button>
            </div>
            <MiniStamps stamps={s.stamps} size="medium" />
            <p className="home-tile__note">{stampsNote(s.stamps, 'More stamps on')}</p>
          </section>
        )}
        {s.post.length > 0 && (
          <section className="home-tile home-tile--compact" aria-labelledby="board-post-title">
            <h2 className="home-tile__title" id="board-post-title">
              In the post
            </h2>
            <PostList post={s.post} onOpen={(id) => s.onOpenConversation(id, 'in_the_post_open')} />
          </section>
        )}
      </div>
      {s.resendMessage && (
        <p className="home-panel__status" role="status">
          {s.resendMessage}
        </p>
      )}
    </div>
  );
}

const ROTATE_MS = 8000;

function WhatsNew({ features, act, onOpenStampBook }: Shared) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (features.length < 2 || paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => setI((n) => (n + 1) % features.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [features.length, paused]);
  const f = features[i % features.length]!;
  return (
    <section
      className="board-news"
      aria-roledescription="carousel"
      aria-label="New in toiyp"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="board-news__blob" aria-hidden="true" />
      <span className="board-news__envelope" aria-hidden="true">
        <span className="board-news__flap" />
        <PostageStamp cls="express" size="small" />
      </span>
      <div className="board-news__tags">
        <span className="home-chip home-chip--new">New in toiyp</span>
        {f.plus && <span className="home-chip home-chip--outline">Plus</span>}
      </div>
      <h2 className="board-news__title" aria-live="polite">
        {f.title}
      </h2>
      <p className="board-news__body">{f.body}</p>
      <div className="board-news__foot">
        <button
          type="button"
          className="home-btn home-btn--paper"
          onClick={() => onOpenStampBook('whats_new', { feature: f.id })}
        >
          {f.cta}
        </button>
        {features.length > 1 && (
          <span className="board-news__dots">
            {features.map((x, n) => (
              <button
                key={x.id}
                type="button"
                className={`board-news__dot${n === i ? ' is-on' : ''}`}
                aria-label={`Show ${x.title}`}
                aria-current={n === i ? 'true' : undefined}
                onClick={() => {
                  act('whats_new_next', { feature: x.id });
                  setI(n);
                }}
              />
            ))}
          </span>
        )}
      </div>
    </section>
  );
}

interface TodoRow {
  id: string;
  Icon: typeof Mail;
  title: string;
  note: string;
  cta: string;
  onClick: () => void;
  expanded?: boolean;
  disabled?: boolean;
}

function ThingsToDo({
  progress,
  inviting,
  handle,
  doStep,
  conversations,
  giftable,
  onGift,
  tasks,
  doTask,
  pushBusy,
  act,
}: Shared & { conversations: PanelConversation[]; giftable: boolean; onGift: (relationshipId: string) => void }) {
  const [showDone, setShowDone] = useState(false);
  const rows: TodoRow[] = [];

  for (const t of tasks.slice(0, MAX_TASKS)) {
    rows.push({ id: t.id, ...taskCopy(t), onClick: () => doTask(t), disabled: t.kind === 'push' && pushBusy });
  }
  for (const key of progress?.undone ?? []) {
    const it = itemOf(key);
    rows.push({
      id: key,
      Icon: it.Icon,
      title: it.title,
      note: it.note,
      cta: STEP[key].cta.split(' ')[0]!,
      onClick: () => doStep(key, 'todo_item'),
      expanded: key === 'invited' ? inviting : undefined,
    });
  }
  // Someone you write to, for the gift nudge: the most recent, never the Post Office.
  const giftPeer = giftable
    ? conversations
        .filter((c) => !c.hidden && c.peer && c.peer.handle !== POST_OFFICE_HANDLE && c.last)
        .sort((a, b) => (b.last!.sealedAt ?? '').localeCompare(a.last!.sealedAt ?? ''))[0]
    : undefined;
  if (giftPeer) {
    rows.push({
      id: 'gift',
      Icon: Gift,
      title: `Gift ${handleName(giftPeer.peer!)} a stamp book`,
      note: 'Tuck it in with your next letter.',
      cta: 'Choose',
      onClick: () => onGift(giftPeer.relationshipId),
    });
  }

  const done = progress?.done ?? [];
  if (rows.length === 0 && done.length === 0) return null;

  return (
    <section className="home-tile board-todo" aria-labelledby="board-todo-title">
      <div className="home-tile__head">
        <h2 className="home-tile__title" id="board-todo-title">
          Little things to do
        </h2>
        {progress && (
          <span className="home-tile__count">
            {progress.done.length} of {progress.total} done
          </span>
        )}
      </div>
      <ul className="board-todo__list">
        {rows.map((r) => (
          <li key={r.id} className="board-todo__row">
            <span className="board-todo__icon" aria-hidden="true">
              <r.Icon size={18} />
            </span>
            <span className="board-todo__text">
              <span className="board-todo__title">{r.title}</span>
              <span className="board-todo__note">{r.note}</span>
            </span>
            <button
              type="button"
              className="home-pill"
              aria-expanded={r.expanded}
              disabled={r.disabled}
              onClick={r.onClick}
            >
              {r.cta}
            </button>
            {r.id === 'invited' && inviting && handle && (
              <div className="board-todo__invite">
                <InviteChoices
                  handle={handle}
                  onStarted={(id) => {
                    window.location.hash = `#/c/${id}`;
                  }}
                />
              </div>
            )}
          </li>
        ))}
        {done.length > 0 && (
          <li className="board-todo__row board-todo__row--done">
            <span className="board-todo__icon board-todo__icon--done" aria-hidden="true">
              <Check size={18} />
            </span>
            {showDone ? (
              <ul className="board-todo__done-list">
                {done.map((k) => (
                  <li key={k}>{itemOf(k).title}</li>
                ))}
              </ul>
            ) : (
              <span className="board-todo__text board-todo__done-line">{doneLine(done)}</span>
            )}
            <button
              type="button"
              className="home-link home-link--quiet"
              aria-expanded={showDone}
              onClick={() => {
                if (!showDone) act('show_done');
                setShowDone((v) => !v);
              }}
            >
              {showDone ? 'Hide' : 'Show'}
            </button>
          </li>
        )}
      </ul>
    </section>
  );
}

function TaskRow({ task, busy, onDo }: { task: TodayTask; busy: boolean; onDo: (task: TodayTask) => void }) {
  const c = taskCopy(task);
  return (
    <li className="board-todo__row">
      <span className="board-todo__icon" aria-hidden="true">
        <c.Icon size={18} />
      </span>
      <span className="board-todo__text">
        <span className="board-todo__title">{c.title}</span>
        <span className="board-todo__note">{c.note}</span>
      </span>
      <button type="button" className="home-pill" disabled={task.kind === 'push' && busy} onClick={() => onDo(task)}>
        {c.cta}
      </button>
    </li>
  );
}

/** "Confirm your email, write your first letter" — the done items in a line. */
function doneLine(done: Item[]): string {
  const s = done.map((k, n) => (n === 0 ? itemOf(k).title : itemOf(k).title.toLowerCase())).join(', ');
  return s;
}

function QrCard({ handle, onShare, onShowQr }: { handle: string; onShare: () => void; onShowQr: () => void }) {
  const { data: link } = useLastingLinkQuery();
  const matrix = useMemo(() => (link ? qrMatrix(withVia(link.url, 'qr')) : null), [link]);
  const address = toiypAddress(handle);
  return (
    <section className="board-qr" aria-labelledby="board-qr-title">
      <h2 className="board-qr__label" id="board-qr-title">
        What’s your toiyp?
      </h2>
      {matrix && (
        <button type="button" className="board-qr__code" onClick={onShowQr} aria-label="Show my toiyp code full screen">
          <svg viewBox={`0 0 ${matrix.length} ${matrix.length}`} aria-hidden="true" shapeRendering="crispEdges">
            <path d={qrPath(matrix)} fill="currentColor" />
          </svg>
        </button>
      )}
      <p className="board-qr__address">{address}</p>
      <button type="button" className="home-btn home-btn--ink home-btn--block" onClick={onShare}>
        Share my toiyp
      </button>
    </section>
  );
}

/* ── Shared bits ─────────────────────────────────────────────────────────── */

function dayMonth(iso: string, month: 'long' | 'short' = 'long'): string {
  return new Date(iso).toLocaleDateString([], { day: 'numeric', month });
}

function stampsNote(s: StampSummary, lead: string): string {
  if (s.nextAllowanceOn) return `${lead} ${dayMonth(s.nextAllowanceOn)}.`;
  return 'Print them on any letter. The class mark stays in the corner.';
}

/**
 * A few stamps from the book, and an empty slot — the book at a glance. Custom
 * designs show as themselves (as on the stamp book page), carrying Express
 * when there are Express stamps to spend; otherwise the plain class stamps.
 */
function MiniStamps({ stamps, size }: { stamps: StampSummary; size: 'small' | 'medium' | 'sheet' }) {
  const express = Math.min(stamps.express, 3);
  const first = Math.min(stamps.firstPasses, 3 - express);
  return (
    <div className="home-stamps" role="img" aria-label={stampsLabel(stamps)}>
      {stamps.designs.length > 0
        ? stamps.designs.map((d) => (
            <PostageStamp key={d.id} cls={stamps.express > 0 ? 'express' : 'second'} size={size} design={d} />
          ))
        : null}
      {stamps.designs.length === 0 &&
        Array.from({ length: express }, (_, i) => <PostageStamp key={`e${i}`} cls="express" size={size} />)}
      {stamps.designs.length === 0 &&
        Array.from({ length: first }, (_, i) => <PostageStamp key={`f${i}`} cls="first" size={size} />)}
      <span className={`home-stamps__slot home-stamps__slot--${size}`} />
    </div>
  );
}

function stampsLabel(s: StampSummary): string {
  const parts: string[] = [];
  if (s.counter) parts.push(`${s.express} Express stamp${s.express === 1 ? '' : 's'}`);
  if (s.designs.length > 0) parts.push(s.designs.map((d) => d.name).join(', '));
  if (s.firstPasses > 0) parts.push(`${s.firstPasses} 1st class`);
  return parts.join(', ');
}

function PostList({ post, onOpen }: { post: PostItem[]; onOpen: (relationshipId: string) => void }) {
  return (
    <ul className="home-post">
      {post.slice(0, 3).map((p) => (
        <li key={`${p.kind}-${p.relationshipId}`}>
          <button type="button" className="home-post__row" onClick={() => onOpen(p.relationshipId)}>
            <span className="home-post__avatar">
              <Avatar name={p.peer.displayName ?? p.peer.handle ?? '?'} avatar={p.peer.avatar} />
            </span>
            <span className="home-post__text">
              {p.kind === 'coming' ? (
                <>
                  <span>
                    <strong>{handleName(p.peer)}</strong> wrote to you
                  </span>
                  <span className="pill pill--eta home-post__eta">arrives {arrivesPhrase(p.deliverAt)}</span>
                </>
              ) : (
                <>
                  <span>
                    <strong>{handleName(p.peer)}</strong> is writing back
                  </span>
                  {p.landedAt && <span className="home-post__note">{landedPhrase(p.landedAt)}</span>}
                </>
              )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** A round date cancel, the way the sorting office franks a letter. Decoration. */
function Postmark({ date }: { date: Date }): ReactNode {
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase();
  return (
    <span className="desk-postmark" aria-hidden="true">
      <span className="desk-postmark__inner">
        <span className="desk-postmark__small">TOIYP POST</span>
        <span className="desk-postmark__day">{day}</span>
        <span className="desk-postmark__small">{date.getFullYear()}</span>
      </span>
    </span>
  );
}

